import { Scale, ShieldCheck, Coins, LineChart, Percent } from "lucide-react";
import { useLeadModal } from "@/context/LeadModalContext";
import Reveal from "@/components/landing/Reveal";

export default function PerformanceSection() {
  const { openModal } = useLeadModal();

  return (
    <section
      id="performance"
      data-testid="performance-section"
      className="relative py-20 sm:py-28"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* How it works card — risk management, no performance numbers */}
        <Reveal>
        <div
          data-testid="performance-card"
          className="overflow-hidden rounded-3xl border border-yellow-400/20 bg-[#0a0a0a]/90 p-5 backdrop-blur-xl sm:p-8 lg:p-10"
          style={{ boxShadow: "0 0 50px rgba(255,215,0,0.08)" }}
        >
          {/* Badge */}
          <div className="mb-5 flex items-center gap-2">
            <span className="h-px w-8 bg-gradient-to-r from-transparent to-yellow-400" />
            <span className="font-heading text-[11px] font-bold uppercase tracking-[0.3em] text-yellow-300">
              Come Funziona
            </span>
          </div>

          {/* Big headline */}
          <h2
            data-testid="performance-title"
            className="font-heading text-3xl font-black uppercase leading-[1] tracking-tight text-white sm:text-4xl lg:text-5xl xl:text-6xl"
          >
            Un approccio <span className="text-gradient-gold">strutturato</span> alla{" "}
            <span className="text-gradient-cyan">gestione del rischio</span>.
          </h2>

          <p className="mt-4 max-w-3xl font-body text-base text-gray-400 sm:mt-5 sm:text-lg">
            Aurum AI applica stop loss automatici e limiti di esposizione su ogni operazione,
            direttamente sul tuo conto broker personale, secondo parametri operativi preimpostati.
          </p>

          {/* How-it-works steps */}
          <div className="mt-7 grid grid-cols-1 gap-4 sm:mt-8 sm:grid-cols-3">
            <div
              data-testid="performance-step-analysis"
              className="rounded-2xl border border-white/10 bg-black/40 p-4"
            >
              <LineChart className="h-5 w-5 text-cyan-300" />
              <div className="mt-2 font-heading text-sm font-bold uppercase tracking-wide text-white">
                Analisi di mercato
              </div>
              <div className="mt-1 font-body text-xs text-gray-400">
                Il software monitora Gold e Forex in continuo, senza intervento manuale.
              </div>
            </div>
            <div
              data-testid="performance-step-riskmanagement"
              className="rounded-2xl border border-white/10 bg-black/40 p-4"
            >
              <ShieldCheck className="h-5 w-5 text-yellow-300" />
              <div className="mt-2 font-heading text-sm font-bold uppercase tracking-wide text-white">
                Gestione del rischio
              </div>
              <div className="mt-1 font-body text-xs text-gray-400">
                Stop loss e dimensionamento della posizione impostati su ogni singola operazione.
              </div>
            </div>
            <div
              data-testid="performance-step-control"
              className="rounded-2xl border border-white/10 bg-black/40 p-4"
            >
              <Percent className="h-5 w-5 text-cyan-300" />
              <div className="mt-2 font-heading text-sm font-bold uppercase tracking-wide text-white">
                Controllo totale
              </div>
              <div className="mt-1 font-body text-xs text-gray-400">
                Depositi e prelievi sono effettuabili in ogni momento in autonomia.
              </div>
            </div>
          </div>
        </div>
        </Reveal>

        {/* MINIMUM DEPOSIT INFO — neutral */}
        <Reveal delay={100}>
        <div
          data-testid="performance-filter"
          className="luminous-border-cyan relative mt-6 overflow-hidden rounded-3xl p-6 sm:mt-8 sm:p-8"
        >
          <div className="grid grid-cols-1 items-center gap-6 lg:grid-cols-3 lg:gap-8">
            <div className="lg:col-span-2">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/5 px-3 py-1 backdrop-blur-sm">
                <Scale className="h-3.5 w-3.5 text-cyan-300" />
                <span className="font-heading text-[10px] font-bold uppercase tracking-wider text-cyan-300">
                  Deposito minimo
                </span>
              </div>
              <p
                data-testid="performance-filter-text"
                className="font-body text-base text-gray-200 sm:text-lg"
              >
                Il <span className="font-semibold text-yellow-300">deposito minimo richiesto sul tuo broker personale è di 250€</span>.
                I fondi rimangono depositati sul tuo conto. Depositi e prelievi sono effettuabili
                in ogni momento in autonomia.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div
                data-testid="filter-min-deposit"
                className="rounded-2xl border border-yellow-400/30 bg-yellow-400/5 p-4 text-center"
              >
                <Coins className="mx-auto h-5 w-5 text-yellow-300" />
                <div className="mt-2 font-heading text-2xl font-black text-gradient-gold sm:text-3xl">
                  250€
                </div>
                <div className="mt-1 font-body text-[10px] font-medium uppercase tracking-wider text-gray-400">
                  Deposito minimo
                </div>
              </div>
              <div
                data-testid="filter-safety"
                className="rounded-2xl border border-cyan-400/30 bg-cyan-400/5 p-4 text-center"
              >
                <ShieldCheck className="mx-auto h-5 w-5 text-cyan-300" />
                <div className="mt-2 font-heading text-base font-black text-cyan-300 sm:text-lg">
                  Sul tuo broker
                </div>
                <div className="mt-1 font-body text-[10px] font-medium uppercase tracking-wider text-gray-400">
                  Capitale sul tuo conto
                </div>
              </div>
            </div>
          </div>
        </div>
        </Reveal>

        {/* CTA */}
        <div className="mt-10 flex justify-center sm:mt-12">
          <button
            type="button"
            onClick={openModal}
            data-testid="performance-cta"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-yellow-300 via-yellow-400 to-yellow-600 px-8 py-4 font-heading text-sm font-black uppercase tracking-wider text-black shadow-[0_0_30px_rgba(255,215,0,0.45)] transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_50px_rgba(255,215,0,0.7)] sm:text-base"
          >
            Scopri come funziona
            <span className="transition-transform group-hover:translate-x-1">→</span>
          </button>
        </div>
      </div>
    </section>
  );
}
